import React, { useEffect, useRef } from 'react';
import NotFoundUI from 'src/components/NotFoundUI';
import TruncateTextCmp from 'src/components/TruncateText';

export interface IActivityDisplayItem {
    id: number | string;
    title: string;
    dotColor: string;
    displayMessage?: string;
    timeLabel: string;
}

interface IProps {
    items: IActivityDisplayItem[];
    isLoading: boolean;
    listTitle?: string;
    listClassName?: string;
    emptyStateDesc?: string;
    // Infinite-scroll (optional, backward-compatible). When `onLoadMore` + `hasMore` are provided,
    // an IntersectionObserver sentinel at the bottom of the list auto-fetches the next page as it
    // scrolls into view. Callers that omit these keep the previous single-page behaviour.
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
}

// Three skeleton rows, used for both the initial load and the load-more spinner.
const ActivityRowSkeleton = () => (
    <>
        {[...Array(3)].map((_, index) => (
            <li key={index} className="rounded-md border border-utilityGray200 p-3 space-y-0.5 animate-pulse">
                <div className="flex items-center gap-x-1.5">
                    <p className="w-2 h-2 rounded-full flex-none custom-loading" />
                    <p className="truncate text-grayLight900 text-xs font-semibold flex-1 custom-loading">lorem ipsum</p>
                    <p className="text-grayLight600 text-[10px] leading-[14px] font-normal flex-none custom-loading">lorem ipsum</p>
                </div>
                <p className="text-grayLight600 text-xs font-medium pl-3.5 custom-loading w-fit">lorem ipsum dolar sit amet</p>
            </li>
        ))}
    </>
);

const ActivityMessage = ({ displayMessage }: { displayMessage: string }) => {
    const messageRef = useRef<HTMLParagraphElement>(null);

    return (
        <TruncateTextCmp message={displayMessage} refName={messageRef} parentClassName="max-w-[400px] w-max">
            <p ref={messageRef} className="text-grayLight600 text-xs font-medium pl-3.5 line-clamp-2 break-words">
                {displayMessage}
            </p>
        </TruncateTextCmp>
    );
};

// Walk up from the sentinel to the nearest scrollable ancestor — the dropdown's actual scroll
// container is the tooltip wrapper (`.tooltip-content-with-scroll-wrap`, which carries the
// max-height + overflow), NOT this <ul>. Using that ancestor as the observer root is what makes
// the sentinel fire when it scrolls into view inside the tooltip.
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let current = node?.parentElement ?? null;

    while (current) {
        const { overflowY } = window.getComputedStyle(current);

        if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
            return current;
        }
        current = current.parentElement;
    }

    return null;
};

const ActivityDropdownList = ({
    items,
    isLoading,
    listTitle,
    listClassName = '',
    emptyStateDesc = 'There is no activity recorded for this quote yet.',
    hasMore = false,
    onLoadMore,
    isLoadingMore = false,
}: IProps) => {
    const sentinelRef = useRef<HTMLLIElement>(null);

    // IntersectionObserver sentinel for auto-load-more.
    useEffect(() => {
        const sentinel = sentinelRef.current;

        if (!sentinel || !onLoadMore || !hasMore) return;

        const root = getScrollParent(sentinel);

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore) {
                    onLoadMore();
                }
            },
            { root: root ?? null, threshold: 0.1, rootMargin: '0px 0px 80px 0px' }
        );

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [hasMore, isLoading, isLoadingMore, onLoadMore, items.length]);

    return (
        <>
            {items.length > 0 && listTitle ? <p className="text-xs text-grayLight900 font-medium">{listTitle}</p> : null}
            <ul className={`space-y-2 tooltip-content-with-scroll ${listClassName}`}>
                {isLoading ? (
                    <ActivityRowSkeleton />
                ) : items.length === 0 ? (
                    <NotFoundUI title="No Latest Activity" desc={emptyStateDesc} containerClassName="!h-auto !my-4" iconClassName="!hidden" />
                ) : (
                    <>
                        {items.map(({ id, title, dotColor, displayMessage, timeLabel }) => (
                            <li key={id} className="rounded-md border border-utilityGray200 p-3 space-y-0.5">
                                <div className="flex items-center gap-x-1.5">
                                    <p className={`w-2 h-2 rounded-full flex-none ${dotColor}`} />
                                    <p className="truncate text-grayLight900 text-xs font-semibold flex-1">{title}</p>
                                    <p className="text-grayLight600 text-[10px] leading-[14px] font-normal flex-none">{timeLabel}</p>
                                </div>
                                {displayMessage && <ActivityMessage displayMessage={displayMessage} />}
                            </li>
                        ))}
                        {isLoadingMore ? <ActivityRowSkeleton /> : hasMore ? <li ref={sentinelRef} className="h-px w-full" aria-hidden="true" /> : null}
                    </>
                )}
            </ul>
        </>
    );
};

export default ActivityDropdownList;
